<!--
  <DatesHeatChart
    title="My title"
    :chardata="chardata"
  >

  chardata = {
    name: 'Heat',
    start_date:'2024-01-1',
    end_date:'2024-12-31',
    entries: {
      '2024-01-3': 15,
      '2024-02-10': 4,
      '2024-05-15': 0,
      '2024-11-21': 60,
    },
  }
-->

<template>
  <highcharts
    :options="chart_options"
    ref="chart"
    style="width: 100%; height: 100%;"
  />
</template>
  
  
<style lang="scss">
</style>

  
<script>
  import { mapState } from 'vuex'
  import customThemes from '@/plugins/themes.js'
  export default {
    name: "DatesHeatChart",
  
    props: {
      title: {required: true},
      subtitle: {required: false},
      chardata: {required: true},
    },

    data: () => ({
        dateFormat: {year: 'numeric', month: 'numeric', day: 'numeric'},
        start: null,
        end: null,
    }),
  
    computed: {
      ...mapState(['currentTheme']),

      chart_options() {
        return ({
          chart: {
                type: 'heatmap',
          },

          title: {
                text: '',
          },

          xAxis: {
                categories:this.getXAxis(this.chardata.start_date,this.chardata.end_date)
          },

          yAxis: {
                categories: this.days,
                title: null,
                reversed: true
          },

          colorAxis: {
              min: 0,
              max: this.max_value(this.chardata.entries),
              minColor: '#eeeeee',
              maxColor: customThemes[this.currentTheme].light.primary
          },

          legend: {
              align: 'right',
              layout: 'vertical',
              padding: 0,
              verticalAlign: 'top',
          },

          series: [{
              borderWidth: 4,
              borderColor: 'white',
              data: this.processItems(this.chardata.entries),
              turboThreshold: 5000,
          }],

          tooltip: {
            headerFormat: '',
            pointFormat: '<b style="font-weight: black;">{point.custom.extraInformation}</b><br>'+'<b>{point.value}</b><br>'
          },

          credits: {
            enabled: false
          },

          responsive: {
            rules: [{
              condition: {
                maxWidth: 500
              },
              chartOptions: {
                yAxis: {
                  labels: {
                    format: '{substr value 0 1}'
                  }
                }
              }
            }]
          }
        });
      },

      months() {
        return [
          this.$vuetify.lang.translator('month_names.january'),
          this.$vuetify.lang.translator('month_names.february'),
          this.$vuetify.lang.translator('month_names.march'),
          this.$vuetify.lang.translator('month_names.april'),
          this.$vuetify.lang.translator('month_names.may'),
          this.$vuetify.lang.translator('month_names.june'),
          this.$vuetify.lang.translator('month_names.july'),
          this.$vuetify.lang.translator('month_names.august'),
          this.$vuetify.lang.translator('month_names.september'),
          this.$vuetify.lang.translator('month_names.october'),
          this.$vuetify.lang.translator('month_names.november'),
          this.$vuetify.lang.translator('month_names.december')
        ];
      },

      month() {
        return this.months.map(it => it.slice(0,3));
      },

      days() {
        return [
          'week_days.monday',
          'week_days.tuesday',
          'week_days.wednesday',
          'week_days.thursday',
          'week_days.friday',
          'week_days.saturday',
          'week_days.sunday'
        ].map(day => this.$vuetify.lang.translator(day));
      },
    },
    methods: {
      //Gets start date and end date
      datesValues() {
        const sort_items = ([] || this.chardata.entries || []).sort();
        const today = new Date(Date.now())
        //Start date
        let start = new Date(today.getFullYear(), 0, 1);
        // Check if exist starting date
        // Or take the first date of the entries 
        // Or stay as the first day of the current year
        if (this.chardata.start_date) {
          start = new Date(this.chardata.start_date);
        } else if (this.chardata.entries.length > 0) {
          start = new Date(sort_items[0]);
        }

        //End date
        let end = new Date(start + 1,0,1);
        // Check if exist ending date
        // Or take the last date of the entries 
        // Or stay as the last day of the current year
        if (this.chardata.end_date) {
          end = new Date(this.chardata.end_date);
        } else if (this.chardata.entries.length > 0) {
          end = new Date(sort_items[0]);
        }
        this.start = start.toString()
        this.end = end.toString()
      },

      // Make a table filled with value 0
      getDefaultValues() {
        this.datesValues();
          
        let date = new Date(this.start);
        let end = new Date(this.end);
          
        const start_year = date.getFullYear();

        let week = {};
        while(date <= end){
          //Getting the correct week day, Date uses Sunday as first day of the week
          const week_day = ((date.getDay() - 1) + 7) % 7;

          const week_num = this.getWeekNumber(date) + (date.getFullYear() != start_year ? 52*(date.getFullYear() - start_year) : 0)
          const week_str = date.toLocaleDateString(undefined, this.dateFormat).split('T')[0];
          
          week[week_str]={
            y: week_day,
            x: week_num,
            value: 0,
            custom: {extraInformation: week_str}
          };

          date.setDate(date.getDate() + 1);
            
        }
        return week;
      },

      processItems(items) {
        let values = this.getDefaultValues();

        for (let [day_str,count] of Object.entries((items || {}))) {
          let current_date = new Date(day_str);
          
          const str = current_date.toLocaleDateString(undefined, this.dateFormat).split('T')[0]
          values[str].value = count;
        }
        return Object.values(values);
      },

      //Getting the max values of the entries, if there is no items, the max will be 1, making withe the value 0
      max_value(data) {
        let max = 1;
        Object.entries(data || {}).forEach(([key,item]) => max = ( item > max ? item : max));
        return max;
      },

      getXAxis(start_date,end_date) {
        // Non values of start and/or end, calculate it and save it on 'start' and 'end' on data values
        if (!((start_date || this.start) && (end_date || this.end))) {
          this.datesValues();
        }
        
        const start = new Date(start_date || this.start);
        const end = new Date(end_date || this.end);


        let year = start.getFullYear();
        let month = start.getMonth();
        
        let year_month = new Array(52*(end.getFullYear() - start.getFullYear() + 1)).fill(' ');
        
        while (year <= end.getFullYear()) {
          
          let year_diff = end.getFullYear() - year;
          
          while (month < 12) {
            let pos = this.getWeekNumber(new Date(year,month,1));
            year_month[pos+52*year_diff] = this.month[month].slice(0,3)
            month += 1;
          }
          
          month = 0;
          year += 1;
        }
        return year_month.concat(new Array(5).fill(' '));
      },

      getDay(date) {
        //Date uses Sunday as the first day of the week, we use Monday as the first day of the week
        return ((date.getDay() - 1) + 7) % 7;
      },

      getWeekNumber(date) {
        // Copy date so don't modify original
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        // Make Sunday's day number 7
        d.setDate(d.getDate() + 4 - (d.getDay()||7));
        
        
        // Get first day of year
        const yearStart = new Date(Date.UTC(d.getFullYear(),0,1));
        // Calculate full weeks to nearest Thursday
        const weekNo = Math.ceil(( ( (d - yearStart) / 86400000) + 1)/7);

        //Problems with the first and last week of the year
        if(date.getMonth() < 1 && weekNo > 5) {
          return 0;
        } else if (yearStart.getDay() > date.getDay() && date.getMonth() >= 11 && (weekNo - 2) < 0) {
          return 53;
        }
        // Return array of year and week number
        return weekNo;
      },
    },
  };
</script>

